import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { uploadFileToS3, updateManagementSubTask } from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

const AddDocumentModal = ({ onClose, subtaskId, existingDocs = [], onSuccess }) => {
    const queryClient = useQueryClient();
    const [selectedFile, setSelectedFile] = useState(null);
    const { register, handleSubmit, formState: { errors } } = useForm();

    // Mutation to update the subtask with the new document list
    const updateSubtaskMutation = useMutation({
        mutationFn: (payload) => updateManagementSubTask(subtaskId, payload),
        onSuccess: () => {
            toast.success('Document added successfully');
            queryClient.invalidateQueries(['managementTasks']); // Fixed query key to plural
            if (onSuccess) onSuccess();
            onClose();
        },
        onError: (error) => {
            console.error(error);
            toast.error(error?.response?.data?.message || 'Failed to update subtask');
        }
    });

    // Mutation to upload file to S3
    const uploadMutation = useMutation({
        mutationFn: ({ file }) => uploadFileToS3(file),
        onSuccess: (data, variables) => {
            console.log('Upload Response Data:', data);

            const uploadedFile = data?.data?.files?.[0];
            if (uploadedFile && uploadedFile.presignedUrl) {
                const newDoc = {
                    name: variables.docName,
                    url: uploadedFile.presignedUrl
                };
                // Append-only: send the new file only. API keeps existing docs.
                updateSubtaskMutation.mutate({ docs: [newDoc] });
            } else {
                console.error('File upload failed: Invalid response structure', data);
                toast.error('File upload failed: No URL returned');
            }
        },
        onError: (error) => {
            console.error('Upload Error:', error);
            toast.error('Failed to upload file');
        }
    });

    const onSubmit = (data) => {
        if (!selectedFile) {
            toast.error('Please select a file');
            return;
        }

        // Pass file and docName as an object to mutation
        uploadMutation.mutate({ file: selectedFile, docName: data.docName });
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    return (
        <Modal
            name="Add Document"
            width={40}
            onClose={onClose}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                    <FormInput
                        label="Document Name"
                        register={register}
                        register_key="docName"
                        type="text"
                        isRequired={true}
                        width={100}
                        placeholder="e.g. Site Plan"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Upload File</label>
                    <input
                        type="file"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-full file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                    />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                        type="button"
                        onClick={onClose}
                        className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={uploadMutation.isLoading || updateSubtaskMutation.isLoading}
                        className="bg-[#346C75] text-white px-8 py-2 rounded hover:bg-[#2a565e]"
                    >
                        {uploadMutation.isLoading ? 'Uploading...' :
                            updateSubtaskMutation.isLoading ? 'Saving...' : 'Add Document'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default AddDocumentModal;
